import { notifier } from "@NextSkyTheme/notification";

const SHARE_CART_PARAM = 'sc';
const SHARE_CART_VERSION = 1;

resolveSharedCartLink();

function getCartPath() {
  return window.routes?.cart_url || '/cart';
}

function getRoutePath(routeName, fallbackPath) {
  if (window.routes?.[routeName]) {
    return window.routes[routeName];
  }

  const cartPath = getCartPath().replace(/\/$/, '');

  if (cartPath.endsWith('/cart')) {
    return cartPath.replace(/\/cart$/, fallbackPath);
  }

  return fallbackPath;
}

function getStoreRootPath() {
  const cartPath = getCartPath().replace(/\/$/, '');

  if (cartPath.endsWith('/cart')) {
    return `${cartPath.slice(0, -'/cart'.length) || '/'}`.replace(/\/?$/, '/');
  }

  return '/';
}

function buildCartPermalink(items) {
  const itemParams = [];
  let itemIndex = 0;

  items.forEach((item) => {
    const [variantId, quantity, properties] = item;

    if (!variantId || Number(quantity) <= 0) return;

    const index = itemIndex;

    itemParams.push(
      `items[${index}][id]=${encodeURIComponent(variantId)}`,
      `items[${index}][quantity]=${encodeURIComponent(quantity)}`
    );

    if (properties && typeof properties === 'object' && !Array.isArray(properties)) {
      Object.entries(properties).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') return;

        itemParams.push(
          `items[${itemIndex}][properties][${encodeURIComponent(key)}]=${encodeURIComponent(String(value))}`
        );
      });
    }

    itemIndex += 1;
  });

  if (!itemParams.length) {
    return null;
  }

  const cartPath = getCartPath();
  const addPath = `${getRoutePath('cart_add_url', '/cart/add')}?${itemParams.join('&')}&return_to=${encodeURIComponent(cartPath)}`;
  const clearPath = getRoutePath('cart_clear_url', '/cart/clear');

  return `${window.location.origin}${clearPath}?return_to=${encodeURIComponent(addPath)}`;
}

function encodeShareCartPayload(items) {
  const payload = [SHARE_CART_VERSION, items];
  const bytes = new TextEncoder().encode(JSON.stringify(payload));
  let binary = '';

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function decodeShareCartPayload(value) {
  const base64 = value
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(value.length / 4) * 4, '=');
  const binary = atob(base64);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  const [version, items] = JSON.parse(new TextDecoder().decode(bytes));

  if (version !== SHARE_CART_VERSION || !Array.isArray(items)) {
    throw new Error('Unsupported shared cart link');
  }

  return items;
}

function resolveSharedCartLink() {
  const currentUrl = new URL(window.location.href);
  const sharedCartPayload = currentUrl.searchParams.get(SHARE_CART_PARAM);

  if (!sharedCartPayload) return;

  try {
    const cartUrl = buildCartPermalink(decodeShareCartPayload(sharedCartPayload));

    if (cartUrl) {
      window.location.replace(cartUrl);
    }
  } catch (error) {
    console.error('Failed to resolve shared cart link', error);
  }
}

async function copyTextToClipboard(text) {
  if (navigator.clipboard?.writeText && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch (error) {
      console.warn('Clipboard API copy failed, trying fallback copy', error);
    }
  }

  if (fallbackCopyTextToClipboard(text)) {
    return;
  }

  throw new Error('Copy was blocked by your browser. Please copy the link manually.');
}

function fallbackCopyTextToClipboard(text) {
  if (!document.body || typeof document.execCommand !== 'function') {
    return false;
  }

  const textArea = document.createElement('textarea');

  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.top = '0';
  textArea.style.left = '-9999px';
  textArea.style.opacity = '0';
  textArea.style.pointerEvents = 'none';

  document.body.appendChild(textArea);

  const selection = document.getSelection();
  const selectedRange = selection?.rangeCount ? selection.getRangeAt(0) : null;
  let copied = false;

  try {
    try {
      textArea.focus({ preventScroll: true });
    } catch (error) {
      textArea.focus();
    }

    textArea.select();
    textArea.setSelectionRange(0, textArea.value.length);
    copied = document.execCommand('copy');
  } catch (error) {
    console.warn('Fallback copy failed', error);
  } finally {
    document.body.removeChild(textArea);

    if (selectedRange && selection) {
      selection.removeAllRanges();
      selection.addRange(selectedRange);
    }
  }

  return copied;
}

class ShareCartLink extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
  }

  handleKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.click();
    }
  }

  async handleClick() {
    if (this.hasAttribute('disabled') || this.isGenerating) return;

    this.isGenerating = true;

    try {
      const url = await this.generateCartPermalink();

      if (!url) {
        this.dispatchEvent(new CustomEvent('share-cart-empty', {
          bubbles: true
        }));
        return;
      }

      await copyTextToClipboard(url);

      this.dispatchEvent(new CustomEvent('share-cart-generated', {
        detail: { url },
        bubbles: true
      }));
      notifier.show("Cart link copied to clipboard", "success", 4000);

    } catch (error) {
      this.dispatchEvent(new CustomEvent('share-cart-error', {
        detail: { error },
        bubbles: true
      }));
      notifier.show(error.message, "error", 4000);

    } finally {
      this.isGenerating = false;
    }
  }

  async generateCartPermalink() {
    const response = await fetch('/cart.js', {
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch cart: ${response.status}`);
    }

    const cart = await response.json();

    if (!Array.isArray(cart.items) || cart.items.length === 0) {
      return null;
    }

    const items = cart.items.reduce((shareItems, item) => {
      if (!item.variant_id || item.quantity <= 0) return shareItems;

      const shareItem = [String(item.variant_id), item.quantity];
      const properties = {};

      if (item.properties && typeof item.properties === 'object') {
        Object.entries(item.properties).forEach(([key, value]) => {
          if (value === null || value === undefined || value === '') return;

          properties[key] = String(value);
        });
      }

      if (Object.keys(properties).length) {
        shareItem.push(properties);
      }

      shareItems.push(shareItem);

      return shareItems;
    }, []);

    if (!items.length) {
      return null;
    }

    const url = new URL(getStoreRootPath(), window.location.origin);
    url.searchParams.set(SHARE_CART_PARAM, encodeShareCartPayload(items));

    return url.toString();
  }
}
if (!customElements.get('share-cart-link')) {
  customElements.define('share-cart-link', ShareCartLink);
}


if (window.Shopify?.previewMode) {
  document.addEventListener('share-cart-generated', (event) => {
    console.log('Cart link:', event.detail.url);
  });

  document.addEventListener('share-cart-empty', () => {
    console.log('Cart is empty');
  });

  document.addEventListener('share-cart-error', (event) => {
    console.error('Failed to generate cart link', event.detail.error);
  });
}
